import { createFileRoute, notFound } from "@tanstack/react-router";
import { useEffect } from "react";
import { getPublicSalon, trackLandingEvent } from "@/lib/landing.functions";
import { isTemplateId } from "@/components/landing/shared";
import { EditorialTemplate } from "@/components/landing/EditorialTemplate";
import { StudioTemplate } from "@/components/landing/StudioTemplate";
import { PosterTemplate } from "@/components/landing/PosterTemplate";
import { GalleryTemplate } from "@/components/landing/GalleryTemplate";

export const Route = createFileRoute("/s/$slug")({
  loader: async ({ params }) => {
    const salon = await getPublicSalon({ data: { slug: params.slug } });
    if (!salon) throw notFound();
    return salon;
  },
  head: ({ loaderData }) => {
    const title = loaderData
      ? `${loaderData.name}${loaderData.city ? ` – Friseur in ${loaderData.city}` : ""}`
      : "Salon – SalonTurbo";
    const description = loaderData?.boosted
      ? `Heute noch Termine frei bei ${loaderData.name}. Jetzt anrufen und kurzfristig einen Friseurtermin sichern.`
      : `${loaderData?.name ?? "Salon"}: Termine, Öffnungszeiten und Kontakt auf einen Blick.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        ...(loaderData?.images[0]
          ? [
              { property: "og:image", content: loaderData.images[0] },
              { name: "twitter:image", content: loaderData.images[0] },
            ]
          : []),
      ],
    };
  },
  component: SalonLanding,
  errorComponent: () => (
    <main className="flex min-h-screen items-center justify-center px-4 text-center">
      <p className="text-muted-foreground">Diese Salon-Seite ist gerade nicht verfügbar.</p>
    </main>
  ),
  notFoundComponent: () => (
    <main className="flex min-h-screen items-center justify-center px-4 text-center">
      <p className="text-muted-foreground">Salon nicht gefunden.</p>
    </main>
  ),
});

const TEMPLATES = {
  editorial: EditorialTemplate,
  studio: StudioTemplate,
  poster: PosterTemplate,
  gallery: GalleryTemplate,
} as const;

function SalonLanding() {
  const salon = Route.useLoaderData();
  const { slug } = Route.useParams();

  useEffect(() => {
    void trackLandingEvent({ data: { slug, type: "view" } });
  }, [slug]);

  const templateId = isTemplateId(salon.landing_template) ? salon.landing_template : "editorial";
  const Template = TEMPLATES[templateId];

  return (
    <Template
      salon={salon}
      onCall={() => void trackLandingEvent({ data: { slug, type: "call" } })}
      onBook={() => void trackLandingEvent({ data: { slug, type: "book" } })}
    />
  );
}
